import { ElementType, Question } from "@archetypes/form";
import { isInputQuestionType } from "./element.util";

export function isChoiceTypeQuestion(elementType?: ElementType) {
  return (
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.MultipleDropdownSelect ||
    elementType === ElementType.Ranking
  );
}

export function isHyperLinkSupportedQuestion(elementType?: ElementType) {
  return (
    elementType !== ElementType.Link &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.FileUpload &&
    elementType !== ElementType.ImageUpload &&
    elementType !== ElementType.PageBreak &&
    elementType !== ElementType.Signature &&
    elementType !== ElementType.Repeater
  );
}

export function isOptionTypeQuestion(elementType?: ElementType) {
  return (
    elementType === ElementType.Users ||
    elementType === ElementType.Entities ||
    isChoiceTypeQuestion(elementType)
  );
}
export function isElementTitleEntityOrUser(type: ElementType) {
  return type === ElementType.Users || type === ElementType.Entities;
}

export const shouldRenderPlaceholder = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.SingleChoice &&
    elementType !== ElementType.MultiChoice &&
    elementType !== ElementType.Ranking &&
    elementType !== ElementType.Signature &&
    elementType !== ElementType.DataModel &&
    elementType !== ElementType.Video &&
    elementType !== ElementType.Rating &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.YesNo &&
    elementType !== ElementType.Slider &&
    elementType !== ElementType.FileUpload &&
    elementType !== ElementType.Name &&
    elementType !== ElementType.UniqueId &&
    elementType !== ElementType.PreviewImage &&
    elementType !== ElementType.TermsAndConditons &&
    elementType !== ElementType.Frequency &&
    elementType !== ElementType.Repeater
  );
};

export const shouldRenderRequired = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.UniqueId &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.PreviewImage &&
    elementType !== ElementType.Video
  );
};

export const shouldRenderReadOnly = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.UniqueId &&
    elementType !== ElementType.Formula &&
    elementType !== ElementType.Aggregator
  );
};

export const isSearchableField = (elementType: ElementType): boolean => {
  return (
    elementType !== ElementType.ImageUpload &&
    elementType !== ElementType.PreviewImage &&
    elementType !== ElementType.Video &&
    elementType !== ElementType.Signature &&
    elementType !== ElementType.Expression &&
    elementType !== ElementType.FileUpload &&
    elementType !== ElementType.Frequency &&
    elementType !== ElementType.PageBreak &&
    elementType !== ElementType.Ranking &&
    elementType !== ElementType.DataModel &&
    elementType !== ElementType.Repeater &&
    elementType !== ElementType.RichText &&
    elementType !== ElementType.Slider
  );
};

export const isGroupByField = (elementType: ElementType): boolean => {
  return (
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.MultipleDropdownSelect ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.Entities ||
    elementType === ElementType.Users
  );
};

export const isCompositeField = (elementType: ElementType): boolean => {
  return (
    isSearchableField(elementType) &&
    elementType !== ElementType.RichText &&
    elementType !== ElementType.Currency &&
    elementType !== ElementType.Slider
  );
};

export const shouldRenderHint = (elementType: ElementType): boolean => {
  return elementType !== ElementType.Expression && elementType !== ElementType.UniqueId;
};

export const shouldRenderVisibility = (elementType: ElementType): boolean => {
  return elementType !== ElementType.UniqueId;
};

export const shouldRenderDefaultValue = (elementType: ElementType): boolean => {
  return (
    elementType === ElementType.SingleLineInputBox ||
    elementType === ElementType.MultiLineInputBox ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.MultipleDropdownSelect ||
    elementType === ElementType.Rating ||
    elementType === ElementType.RichText ||
    elementType === ElementType.DatePicker ||
    elementType === ElementType.DateRangePicker ||
    elementType === ElementType.YesNo ||
    elementType === ElementType.TimePicker ||
    elementType === ElementType.Entities ||
    elementType === ElementType.Users ||
    elementType === ElementType.Number ||
    elementType === ElementType.Aggregator
  );
};

export const isQuestionEligibleForDataViewColumn = (questions: Question[]) => {
  return questions.filter((question) => {
    return ![
      ElementType.Expression,
      ElementType.Video,
      ElementType.PageBreak,
      ElementType.PreviewImage,
    ].includes(question.type);
  });
};

export const isFormatTypeQuestion = (elementType: ElementType): boolean => {
  return (
    elementType === ElementType.DatePicker ||
    elementType === ElementType.SingleLineInputBox ||
    elementType === ElementType.Email ||
    elementType === ElementType.Name ||
    elementType === ElementType.SingleChoice ||
    elementType === ElementType.MultiChoice ||
    elementType === ElementType.SingleDropdownSelect ||
    elementType === ElementType.MultipleDropdownSelect
  );
};

export const isPhoneCurrencyEntityOrUserTypePresent = (questions: Question[]) => {
  let isPhoneNoEnabled: boolean = false;
  let isCurrencyTypeEnabled: boolean = false;
  let isEntityEnabled: boolean = false;
  let isUserEnabled: boolean = false;
  for (const question of questions) {
    isPhoneNoEnabled = !isPhoneNoEnabled
      ? question.type === ElementType.MobileNumber
      : isPhoneNoEnabled;
    isCurrencyTypeEnabled = !isCurrencyTypeEnabled
      ? question.type === ElementType.Currency
      : isCurrencyTypeEnabled;
    isEntityEnabled = !isEntityEnabled ? question.type === ElementType.Entities : isEntityEnabled;
    isUserEnabled = !isUserEnabled ? question.type === ElementType.Users : isUserEnabled;
  }
  return { isPhoneNoEnabled, isCurrencyTypeEnabled, isEntityEnabled, isUserEnabled };
};

export const getQuestionsForRulesOrDataView = ({
  questions,
  currentFormId,
  parentConfig,
  dataModelOrRepeaterFormIds = new Set<string>(),
  showOnlyChildLabel = false,
}: {
  questions: Question[];
  currentFormId: string;
  parentConfig?: { label?: string; questionId?: string };
  dataModelOrRepeaterFormIds?: Set<string>;
  showOnlyChildLabel?: boolean;
}) => {
  let formattedQuestions: Question[] = [];

  dataModelOrRepeaterFormIds.add(currentFormId);
  for (const question of questions) {
    if (question.formId !== currentFormId || !isInputQuestionType(question.type)) {
      continue;
    }

    const label = showOnlyChildLabel
      ? question.content.label
      : parentConfig?.label
        ? parentConfig.label + " > " + question.content.label
        : question.content.label;
    const id = parentConfig?.questionId ? parentConfig.questionId + "." + question.id : question.id;

    formattedQuestions.push({
      ...question,
      id,
      content: { ...question.content, label },
    });

    const dataModelOrRepeaterFormId =
      question.content?.dataModel?.formId || question.content?.repeater?.formId;

    if (
      dataModelOrRepeaterFormId &&
      !dataModelOrRepeaterFormIds.has(dataModelOrRepeaterFormId) // We don't want to add same questions again if a form has multiple data models of a particular form
    ) {
      const nestedQuestions = getQuestionsForRulesOrDataView({
        questions,
        currentFormId: dataModelOrRepeaterFormId,
        parentConfig: {
          label,
          questionId: id,
        },
        dataModelOrRepeaterFormIds,
        showOnlyChildLabel,
      });
      formattedQuestions = [...formattedQuestions, ...nestedQuestions];
      dataModelOrRepeaterFormIds.delete(dataModelOrRepeaterFormId);
    }
  }
  return formattedQuestions;
};

type FormatOptions = {
  separator?: string; // default: " > "
  maxDepth?: number; // default: 4
};

/**
 * Formats questions into path-based questionIds and labels, with:
 * - duplicates allowed across different paths
 * - cycle prevention across forms (per path)
 * - self-link (A -> A) treated as a prefix group (Option 2: only one self prefix per path)
 * - depth cap: nothing beyond maxDepth segments is emitted or traversed
 */
export function formatQuestionPaths(
  rootFormId: string,
  allQuestions: Question[],
  options: FormatOptions = {},
): Question[] {
  const { separator = " > ", maxDepth = 4 } = options;

  // Group questions by formId
  const questionsByForm = new Map<string, Question[]>();
  for (const question of allQuestions) {
    if (!question.formId) continue;
    const list = questionsByForm.get(question.formId);
    if (list) list.push(question);
    else questionsByForm.set(question.formId, [question]);
  }

  const out: Question[] = [];

  const canAddSegment = (currentDepth: number) => currentDepth + 1 <= maxDepth;

  const emit = (question: Question, idPath: string[], labelPath: string[]) => {
    const nextDepth = idPath.length + 1;
    if (nextDepth > maxDepth) return false;
    out.push({
      ...question,
      id: [...idPath, question.id].join(separator),
      content: {
        ...question.content,
        label: [...labelPath, question.content.label].join(separator),
      },
    });

    return true;
  };

  const dfs = (
    formId: string,
    idPath: string[],
    labelPath: string[],
    visitedForms: Set<string>,
    usedSelfPrefix: boolean,
  ) => {
    const questions = questionsByForm.get(formId) ?? [];

    for (const question of questions) {
      const emitted = emit(question, idPath, labelPath);
      if (!emitted) continue; // depth cap reached; skip emitting and any traversal from here

      // If we're already at max depth, we can't traverse further anyway.
      const currentDepth = idPath.length + 1;
      if (currentDepth >= maxDepth) continue;

      if (!isInputQuestionType(question.type)) continue;

      const targetFormId =
        question.content?.dataModel?.formId || question.content?.repeater?.formId;

      // ---------- Self-link (A -> A), Option 2 ----------
      if (targetFormId === formId) {
        if (usedSelfPrefix) continue;

        // Self prefix itself consumes one segment; ensure children can fit
        if (!canAddSegment(currentDepth)) continue;

        const selfIdPath = [...idPath, question.id];
        const selfLabelPath = [...labelPath, question.content.label];

        // Emit all siblings except:
        // - same self link
        // - other self links (no stacking)
        for (const sibling of questions) {
          if (sibling.id === question.id) continue;

          const siblingTargetFormId =
            sibling.content?.dataModel?.formId || sibling.content?.repeater?.formId;
          if (siblingTargetFormId === formId) {
            continue; // no stacking self-links
          }

          // Emit sibling under self prefix (checks depth internally)
          const siblingEmitted = emit(sibling, selfIdPath, selfLabelPath);
          if (!siblingEmitted) continue;

          const siblingDepth = selfIdPath.length + 1; // after adding sibling
          if (siblingDepth >= maxDepth) continue;

          // Expand cross-form links under self prefix
          if (siblingTargetFormId) {
            if (visitedForms.has(siblingTargetFormId)) continue;

            const nextVisited = new Set(visitedForms);
            nextVisited.add(siblingTargetFormId);

            dfs(
              siblingTargetFormId,
              [...selfIdPath, sibling.id],
              [...selfLabelPath, sibling.content.label],
              nextVisited,
              true,
            );
          }
        }

        continue;
      }

      // ---------- Normal cross-form link ----------
      if (!targetFormId || visitedForms.has(targetFormId)) continue;

      const nextVisited = new Set(visitedForms);
      nextVisited.add(targetFormId);

      dfs(
        targetFormId,
        [...idPath, question.id],
        [...labelPath, question.content.label],
        nextVisited,
        usedSelfPrefix,
      );
    }
  };

  dfs(rootFormId, [], [], new Set([rootFormId]), false);
  return out;
}
